import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, LockKeyhole, User as UserIcon } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ConnectLogoImage } from "@/components/brand/ConnectLogo";
import ThemeToggle from "@/components/brand/ThemeToggle";

const DEMO = [
  { u: "admin", p: "admin123", desc: "Administrador global" },
  { u: "banca", p: "banca123", desc: "Dueño de banca" },
  { u: "vendedor", p: "vendedor123", desc: "Vendedor de taquilla" },
];

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e?.preventDefault();
    if (!username || !password) {
      toast.error("Ingresa usuario y contraseña");
      return;
    }
    setLoading(true);
    try {
      const u = await login(username.trim(), password);
      toast.success(`Bienvenido, ${u.full_name}`);
      if (u.role === "vendedor" || u.role === "taquilla_operator") navigate("/pos");
      else navigate("/admin");
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 423) {
        toast.error(detail || "Cuenta temporalmente bloqueada por intentos fallidos", { duration: 8000 });
      } else if (status === 429) {
        toast.error("Demasiados intentos. Espera un momento e intenta de nuevo.");
      } else {
        toast.error(detail || "Credenciales inválidas");
      }
    } finally {
      setLoading(false);
    }
  };

  const fill = (u, p) => {
    setUsername(u);
    setPassword(p);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="flex items-center justify-center p-6 lg:p-10 relative">
        <div className="absolute top-4 right-4 z-20">
          <ThemeToggle />
        </div>
        <div className="w-full max-w-md">
          <div className="mb-8">
            <ConnectLogoImage className="w-56 h-auto" />
          </div>

          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-center">Ingresar</h1>
          <p className="text-muted-foreground mt-1 text-sm text-center">Accede al panel de administración o al terminal de venta.</p>

          <Card className="mt-6 p-5 bg-card border-border">
            <form onSubmit={submit} className="space-y-4" data-testid="login-form">
              <div className="space-y-2">
                <Label htmlFor="username">Usuario</Label>
                <div className="relative">
                  <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="username"
                    data-testid="login-username-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="admin, banca, vendedor"
                    autoComplete="username"
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <div className="relative">
                  <LockKeyhole size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    data-testid="login-password-input"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="current-password"
                    className="pl-9"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full"
                data-testid="login-submit-button"
              >
                <LogIn size={16} className="mr-2" />
                {loading ? "Ingresando…" : "Ingresar"}
              </Button>
            </form>

            <Separator className="my-4" />
            <div className="text-xs text-muted-foreground mb-2">Cuentas demo (click para autocompletar):</div>
            <div className="grid grid-cols-1 gap-2">
              {DEMO.map((d) => (
                <button
                  key={d.u}
                  onClick={() => fill(d.u, d.p)}
                  data-testid={`demo-${d.u}`}
                  className="flex items-center justify-between rounded-lg border border-border px-3 py-2 text-left hover:bg-secondary/60"
                  type="button"
                >
                  <div>
                    <div className="text-sm font-medium">{d.u}</div>
                    <div className="text-xs text-muted-foreground">{d.desc}</div>
                  </div>
                  <div className="font-mono text-xs text-muted-foreground">{d.p}</div>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>

      <div className="hidden lg:block relative overflow-hidden border-l border-border bg-[#102747]">
        <div className="absolute inset-0 grid-noise opacity-40" />
        <div className="relative h-full flex flex-col justify-center gap-10 p-10 text-white">
          <div className="space-y-6">
            <ConnectLogoImage variant="dark" className="w-[320px] max-w-full h-auto" />
            <div className="space-y-3 max-w-lg">
              <div className="inline-flex items-center gap-2 rounded-full border border-[#2ed4c7]/40 bg-[#2ed4c7]/10 px-3 py-1 text-xs text-[#2ed4c7] uppercase tracking-widest">
                Loterías · Venezuela
              </div>
              <h2 className="font-display text-3xl xl:text-4xl font-semibold leading-tight text-white">
                Ventas de lotería para taquillas, con control total.
              </h2>
              <p className="text-white/70">
                Triple, Zulia, Granjita, Animalitos y Chance. Doble moneda Bs / USD.
                Jerarquía multi-banca. Sorteos, resultados y pagos auditables.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 max-w-md">
            {[
              { k: "6+", l: "Juegos activos" },
              { k: "Bs+USD", l: "Doble moneda" },
              { k: "Auto", l: "Validación ganadores" },
            ].map((x) => (
              <div key={x.l} className="rounded-xl border border-white/10 bg-white/5 p-3 backdrop-blur">
                <div className="font-display text-xl font-semibold text-[#2ed4c7]">{x.k}</div>
                <div className="text-xs text-white/60">{x.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
